import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, switchMap, take} from 'rxjs';
import {RestFactoryService} from "../../../core/services/rest-factory.service";
import {animate, style, transition, trigger} from "@angular/animations";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatSelectModule} from "@angular/material/select";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatIconModule} from "@angular/material/icon";
import {MatMenuModule} from "@angular/material/menu";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatSnackBar, MatSnackBarModule} from "@angular/material/snack-bar";

interface ReportConfig {
    id: string;
    title: string;
    type: string;
    endpoint: string;
    params: ReportParam[];
}

interface ReportParam {
    name: string;
    title: string;
    type: 'select' | 'text' | 'search';
    endpoint?: string;
    id_column?: string;
    display_column?: string;
    filter_param?: string;
}

@Component({
    selector: 'app-reports',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatTableModule, MatPaginatorModule, MatSortModule, MatProgressBarModule, MatIconModule, MatMenuModule, MatAutocompleteModule, MatSnackBarModule,
    ],
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(10px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(10px)' })),
            ]),
        ]),
    ],
})
export class ReportsComponent implements OnInit {
    private _paginator: MatPaginator;
    @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        if (this.dataSource) {
            this.dataSource.paginator = this._paginator;
        }
    }

    private _sort: MatSort;
    @ViewChild(MatSort) set sort(sort: MatSort) {
        this._sort = sort;
        if (this.dataSource) {
            this.dataSource.sort = this._sort;
        }
    }

    currentStep: 'selection' | 'form' | 'results' = 'selection';
    reportConfigs: ReportConfig[] = [];
    selectedReportConfig: ReportConfig;
    reportForm: FormGroup;
    paramData: { [key: string]: any[] } = {};
    filteredOptions: { [key: string]: Observable<any[]> } = {};

    isLoading: boolean = false;
    isDownloading: boolean = false;

    dataSource: MatTableDataSource<any>;
    displayedColumns: string[] = [];

    private readonly API_REPORTS_CONFIG = '/admin/reports/config';

    constructor(
        private _fb: FormBuilder,
        private _restFactoryService: RestFactoryService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _snackBar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.loadReportConfigs();
    }

    loadReportConfigs(): void {
        this.isLoading = true;
        this.currentStep = 'selection';
        this._restFactoryService.httpPostService(this.API_REPORTS_CONFIG, null)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe(response => {
                if (response && response.ar_results) {
                    this.reportConfigs = response.ar_results;
                }
            });
    }

    selectReport(config: ReportConfig): void {
        this.selectedReportConfig = config;
        this.buildForm();
        this.loadParamOptions();
        this.currentStep = 'form';
    }

    buildForm(): void {
        const formControls = {};
        this.selectedReportConfig.params.forEach(param => {
            formControls[param.name] = [''];
        });
        this.reportForm = this._fb.group(formControls);

        this.selectedReportConfig.params.forEach(param => {
            if (param.type === 'search') {
                this.filteredOptions[param.name] = this.reportForm.get(param.name).valueChanges.pipe(
                    debounceTime(300),
                    distinctUntilChanged(),
                    switchMap(value => (typeof value === 'string') ? this.searchOptions(param, value) : of([]))
                );
            }
        });
    }

    searchOptions(param: ReportParam, query: string): Observable<any[]> {
        if (!query || query.length < 2) {
            return of([]);
        }
        const payload = new FormData();
        payload.append(param.filter_param, query);
        return this._restFactoryService.httpPostService(param.endpoint, payload).pipe(
            map(response => response.ar_results || []),
            catchError(() => of([]))
        );
    }

    loadParamOptions(): void {
        this.paramData = {};
        this.selectedReportConfig.params.forEach(param => {
            if (param.type === 'select' && param.endpoint) {
                this._restFactoryService.httpPostService(param.endpoint, null)
                    .pipe(take(1))
                    .subscribe(response => {
                        if (response && response.ar_results) {
                            this.paramData[param.name] = response.ar_results;
                            this._changeDetectorRef.markForCheck();
                        }
                    });
            }
        });
    }

    generateReport(): void {
        if (this.reportForm.invalid) {
            return;
        }

        this.isLoading = true;
        const rawData = this.reportForm.getRawValue();
        const payload = new FormData();

        this.selectedReportConfig.params.forEach(param => {
            const value = rawData[param.name];
            if (value !== '' && value !== null) {
                let finalValue = value;
                if (param.type === 'search' && typeof value === 'object' && value[param.id_column]) {
                    finalValue = value[param.id_column];
                }
                if (typeof finalValue !== 'object') {
                    payload.append(param.name, finalValue);
                }
            }
        });

        this._restFactoryService.httpPostService(this.selectedReportConfig.endpoint, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe(response => {
                if (response && response.ar_results) {
                    this.displayedColumns = response.ar_columns;
                    this.dataSource = new MatTableDataSource(response.ar_results);
                    this.dataSource.sortingDataAccessor = (item, property) => {
                        const value = item[property];
                        return (typeof value === 'string') ? value.trim().toLowerCase() : value;
                    };
                    this.dataSource.paginator = this._paginator;
                    this.dataSource.sort = this._sort;
                    this.currentStep = 'results';
                }
            });
    }

    resetWizard(): void {
        this.currentStep = 'selection';
        this.dataSource = null;
        this.displayedColumns = [];
        this.reportForm = null;
        this.selectedReportConfig = null;
    }

    goBackToForm(): void {
        this.currentStep = 'form';
        this.dataSource = null;
        this.displayedColumns = [];
    }

    applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    copyTableToClipboard(): void {
        if (navigator.clipboard) {
            const data = this.dataSource.filteredData;
            const headers = this.displayedColumns;
            const tsvData = [
                headers.join('\t'),
                ...data.map(row => headers.map(header => row[header] ?? '').join('\t'))
            ].join('\n');

            navigator.clipboard.writeText(tsvData).then(() => {
                this._snackBar.open('Copied to clipboard!', 'Close', { duration: 3000 });
            }, (err) => {
                console.error('Could not copy text: ', err);
                this._snackBar.open('Failed to copy!', 'Close', { duration: 3000 });
            });
        }
    }

    downloadReport(format: 'csv' | 'tsv' | 'txt'): void {
        if (typeof Worker !== 'undefined') {
            this.isDownloading = true;
            this._changeDetectorRef.markForCheck();
            const worker = new Worker(new URL('@workers/export.worker', import.meta.url), { type: 'module', name: 'export' });
            worker.onmessage = ({ data }) => {
                if (data.error) {
                    console.error('Worker returned an error:', data.error);
                } else {
                    const fileExtension = format;
                    const mimeType = fileExtension === 'csv' ? 'text/csv' : 'text/tab-separated-values';
                    const blob = new Blob([data.result], { type: `${mimeType};charset=utf-8;` });
                    const link = document.createElement('a');
                    const url = URL.createObjectURL(blob);
                    link.setAttribute('href', url);
                    link.setAttribute('download', `report.${fileExtension}`);
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                worker.terminate();
                this.isDownloading = false;
                this._changeDetectorRef.markForCheck();
            };
            worker.onerror = (error) => {
                console.error('Error in export worker:', error);
                worker.terminate();
                this.isDownloading = false;
                this._changeDetectorRef.markForCheck();
            };
            const workerFormat = (format === 'csv') ? 'csv' : 'tsv';
            worker.postMessage({ records: this.dataSource.filteredData, columns: this.displayedColumns, format: workerFormat });
        } else {
            console.error('Web workers are not supported in this browser.');
        }
    }

    displaySearchOption(option: any): string {
        return option && option.ds_title ? option.ds_title : '';
    }
}
